"use client"

import { useState } from "react"
import type { Alternative } from "@/lib/data/alternatives"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"

import { Badge, Button } from "@/components/atoms"
import { SellerAvatar } from "@/components/cells/SellerAvatar/SellerAvatar"
import { Modal } from "@/components/molecules/Modal/Modal"
import { useCartContext } from "@/components/providers"
import { convertToLocale } from "@/lib/helpers/money"
import { toast } from "@/lib/helpers/toast"
import {
  getOfferAmount,
  getOfferStock,
  isPurchasable,
  rankOffers,
  StoreOffer,
} from "@/lib/helpers/buybox"

export const CompareOffersModal = ({
  offers,
  locale,
  variantLabel,
  alternatives = [],
  onClose,
}: {
  offers: StoreOffer[]
  locale: string
  variantLabel?: string
  /** بدائل تجارية للقطعة الأصلية (lib/data/alternatives.ts) */
  alternatives?: Alternative[]
  onClose: () => void
}) => {
  const { addToCart } = useCartContext()
  const [addingId, setAddingId] = useState<string | null>(null)

  const ranked = rankOffers(offers)

  const handleAdd = async (offer: { id: string }) => {
    setAddingId(offer.id)
    try {
      await addToCart({ offerId: offer.id, quantity: 1, countryCode: locale })
      toast.success({ title: "أُضيفت إلى السلة" })
    } catch (error) {
      toast.error({
        title: "تعذّرت الإضافة إلى السلة",
        description: "الكمية المطلوبة غير متوفرة لدى هذا البائع",
      })
    } finally {
      setAddingId(null)
    }
  }

  return (
    <Modal
      heading={alternatives.length ? `قارن العروض والبدائل` : `قارن ${ranked.length} عروض`}
      onClose={onClose}
      data-testid="compare-offers-modal"
    >
      <div className="flex flex-col gap-3 p-4">
        {variantLabel && (
          <p className="label-md text-secondary" data-testid="compare-offers-variant">
            {variantLabel}
          </p>
        )}
        {ranked.map((offer, index) => {
          const amount = getOfferAmount(offer)
          const stock = getOfferStock(offer)
          const buyable = isPurchasable(offer)

          return (
            <div
              key={offer.id}
              className="flex items-center justify-between gap-4 border rounded-sm p-4"
              data-testid="compare-offer-row"
              data-offer-id={offer.id}
            >
              <div className="flex items-center gap-3 min-w-0">
                <SellerAvatar
                  photo={offer.seller?.logo || ""}
                  size={40}
                  alt={offer.seller?.name || "Seller"}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="label-lg text-primary truncate">
                      {offer.seller?.name || "Seller"}
                    </p>
                    {index === 0 && buyable && (
                      <Badge className="bg-positive">أفضل سعر</Badge>
                    )}
                    {offer.seller?.is_premium && (
                      <Badge className="bg-warning">مميّز</Badge>
                    )}
                  </div>
                  <p className="label-sm text-secondary">
                    {buyable ? `متوفر ${stock}` : "نفد المخزون"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="heading-sm text-primary" data-testid="compare-offer-price">
                  {amount !== null
                    ? convertToLocale({
                        amount,
                        currency_code:
                          offer.calculated_price?.currency_code || "eur",
                      })
                    : "—"}
                </span>
                <Button
                  size="small"
                  onClick={() => handleAdd(offer)}
                  disabled={!buyable || addingId !== null}
                  loading={addingId === offer.id}
                  data-testid="compare-offer-add-to-cart"
                >
                  أضف للسلة
                </Button>
              </div>
            </div>
          )
        })}

        {alternatives.length > 0 && (
          <div className="mt-3 flex flex-col gap-3 border-t pt-4" data-testid="compare-alternatives">
            <div>
              <p className="label-lg text-primary">بدائل تجارية ({alternatives.length})</p>
              <p className="label-sm text-secondary">
                ليست من صانع السيارة، سجّلها تجارها بديلاً لهذا الرقم الأصلي — تأكّد من التوافق مع سيارتك.
              </p>
            </div>
            {alternatives.map((a) => {
              const amount = a.amount == null ? null : Number(a.amount)
              const buyable = a.stock > 0 && amount != null
              return (
                <div key={a.offer_id} className="flex items-center justify-between gap-4 border rounded-sm p-4" data-testid="compare-alternative-row">
                  <div className="flex items-center gap-3 min-w-0">
                    {a.thumbnail ? (
                      <img src={a.thumbnail} alt="" className="h-10 w-10 shrink-0 rounded-sm border object-contain" />
                    ) : (
                      <SellerAvatar photo="" size={40} alt={a.seller} />
                    )}
                    <div className="min-w-0">
                      <p className="label-lg text-primary truncate">
                        {a.brand || "تجاري"}{" "}
                        <LocalizedClientLink href={`/products/${a.product_handle}`} className="font-mono text-sm underline" dir="ltr">
                          {a.number}
                        </LocalizedClientLink>
                      </p>
                      <p className="label-sm text-secondary truncate">
                        {a.seller} · {buyable ? `متوفر ${a.stock}` : "نفد المخزون"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="heading-sm text-primary">
                      {amount != null ? convertToLocale({ amount, currency_code: "lyd" }) : "—"}
                    </span>
                    <Button
                      size="small"
                      onClick={() => handleAdd({ id: a.offer_id })}
                      disabled={!buyable || addingId !== null}
                      loading={addingId === a.offer_id}
                      data-testid="compare-alternative-add-to-cart"
                    >
                      أضف للسلة
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}
